import React from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import { Alert, Button, Space, Typography } from "antd";
import { FullscreenExitOutlined, FullscreenOutlined, UndoOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { latLngRingsToGeoJsonFile, type LatLngPoint } from "../geo/plotGeoJson";
import { useNarrowViewport } from "../ux/useNarrowViewport";

type Props = {
  onSave: (file: File) => Promise<void>;
  saving?: boolean;
  /** Map height in px (ignored in fullscreen). */
  height?: number;
};

/** Demo center near EU sample plot (DE timber demo). */
const DEFAULT_CENTER: L.LatLngExpression = [47.995, 9.995];
const DEFAULT_ZOOM = 13;

function collectPolygonRings(group: L.FeatureGroup): LatLngPoint[][] {
  const rings: LatLngPoint[][] = [];
  group.eachLayer((layer) => {
    if (!(layer instanceof L.Polygon)) return;
    const latlngs = layer.getLatLngs();
    const outer = (Array.isArray(latlngs[0]) ? latlngs[0] : latlngs) as L.LatLng[];
    if (!outer || outer.length < 3) return;
    rings.push(outer.map((ll) => ({ lat: ll.lat, lng: ll.lng })));
  });
  return rings;
}

function refreshPlotLabels(group: L.FeatureGroup) {
  let index = 0;
  group.eachLayer((layer) => {
    if (!(layer instanceof L.Polygon)) return;
    index += 1;
    const label = `Plot ${index}`;
    layer.unbindTooltip();
    layer.bindTooltip(label, {
      permanent: true,
      direction: "center",
      className: "plot-map-label",
    });
  });
}

/**
 * Interactive OSM map: draw / edit / delete plot polygons, save as GeoJSON
 * for the existing geolocation document upload API (US-136 / US-302).
 */
export function PlotMapEditor({ onSave, saving = false, height = 360 }: Props) {
  const { t } = useTranslation();
  const isNarrow = useNarrowViewport("md");
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const mapRef = React.useRef<L.Map | null>(null);
  const drawnItemsRef = React.useRef<L.FeatureGroup | null>(null);
  const undoStackRef = React.useRef<L.Layer[]>([]);
  const [polygonCount, setPolygonCount] = React.useState(0);
  const [busy, setBusy] = React.useState(false);
  const [emptyHint, setEmptyHint] = React.useState(false);
  const [fullscreen, setFullscreen] = React.useState(false);
  const [canUndo, setCanUndo] = React.useState(false);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const map = L.map(el, {
      scrollWheelZoom: true,
      tapTolerance: 25,
      touchZoom: true,
      dragging: true,
    }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    mapRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    drawnItemsRef.current = drawnItems;

    const drawControl = new L.Control.Draw({
      position: "topright",
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: { color: "#0b6cff" },
        },
        polyline: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false,
      },
      edit: {
        featureGroup: drawnItems,
        remove: true,
      },
    });
    map.addControl(drawControl);

    const syncCount = () => {
      setPolygonCount(collectPolygonRings(drawnItems).length);
      setEmptyHint(false);
      refreshPlotLabels(drawnItems);
    };

    const onCreated = (event: L.LeafletEvent) => {
      const { layer } = event as L.DrawEvents.Created;
      drawnItems.addLayer(layer);
      undoStackRef.current.push(layer);
      setCanUndo(true);
      syncCount();
    };

    const onDeleted = () => {
      undoStackRef.current = [];
      setCanUndo(false);
      syncCount();
    };

    map.on(L.Draw.Event.CREATED, onCreated);
    map.on(L.Draw.Event.EDITED, syncCount);
    map.on(L.Draw.Event.DELETED, onDeleted);

    const resizeTimer = window.setTimeout(() => map.invalidateSize(), 120);

    return () => {
      window.clearTimeout(resizeTimer);
      map.off(L.Draw.Event.CREATED, onCreated);
      map.off(L.Draw.Event.EDITED, syncCount);
      map.off(L.Draw.Event.DELETED, onDeleted);
      map.remove();
      mapRef.current = null;
      drawnItemsRef.current = null;
      undoStackRef.current = [];
    };
  }, []);

  React.useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const timer = window.setTimeout(() => map.invalidateSize(), 80);
    return () => window.clearTimeout(timer);
  }, [fullscreen]);

  const handleUndo = () => {
    const group = drawnItemsRef.current;
    const layer = undoStackRef.current.pop();
    if (!group || !layer) {
      setCanUndo(false);
      return;
    }
    group.removeLayer(layer);
    setCanUndo(undoStackRef.current.length > 0);
    setPolygonCount(collectPolygonRings(group).length);
    refreshPlotLabels(group);
  };

  const handleSave = async () => {
    const group = drawnItemsRef.current;
    if (!group) return;
    const rings = collectPolygonRings(group);
    if (rings.length === 0) {
      setEmptyHint(true);
      return;
    }
    setEmptyHint(false);
    const file = latLngRingsToGeoJsonFile(rings);
    setBusy(true);
    try {
      await onSave(file);
    } finally {
      setBusy(false);
    }
  };

  const mapHeight = fullscreen ? "min(80vh, 720px)" : isNarrow ? Math.min(height, 280) : height;

  return (
    <Space
      direction="vertical"
      size="small"
      style={{ width: "100%" }}
      className={isNarrow ? "plot-map-editor--narrow" : undefined}
      data-testid="plot-map-editor"
      data-narrow={isNarrow ? "true" : "false"}
    >
      <Alert type="info" showIcon message={t("compliance.mapDraw.hint")} />
      <div
        ref={containerRef}
        data-testid="plot-map"
        className={isNarrow ? "plot-map--touch" : undefined}
        style={{
          height: mapHeight,
          width: "100%",
          borderRadius: 4,
          border: "1px solid #d9d9d9",
          zIndex: 0,
          touchAction: "none",
        }}
      />
      <Space wrap align="center" className="plot-map-actions">
        <Typography.Text type="secondary">
          {t("compliance.mapDraw.polygonCount", { count: polygonCount })}
        </Typography.Text>
        <Button
          className="dds-touch-target"
          icon={<UndoOutlined />}
          disabled={!canUndo}
          onClick={handleUndo}
          data-testid="plot-map-undo"
        >
          {t("compliance.mapDraw.undo")}
        </Button>
        <Button
          className="dds-touch-target"
          icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          onClick={() => setFullscreen((v) => !v)}
          data-testid="plot-map-fullscreen"
        >
          {fullscreen ? t("compliance.mapDraw.exitFullscreen") : t("compliance.mapDraw.fullscreen")}
        </Button>
        <Button
          type="primary"
          className="dds-touch-target"
          disabled={polygonCount === 0}
          loading={busy || saving}
          onClick={handleSave}
          data-testid="plot-map-save"
        >
          {t("compliance.mapDraw.save")}
        </Button>
      </Space>
      {emptyHint && (
        <Typography.Text type="danger">{t("compliance.mapDraw.empty")}</Typography.Text>
      )}
    </Space>
  );
}
